import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { Col, Grid, Row } from '../../ui/Grid';
import ProgressBar from '../../ui/ProgressBar';
import {
  fetchDeepSEAJobStatus,
  setSelectedDeepSEAJob,
} from '../../../actions/DeepSEAActions';
import DeepSEAInsilicoPage from '../InsilicoBrowser/DeepSEAInsilicoPage';
import DeepSEABrowserPage from './DeepSEABrowserPage';
import { useInterval } from '../../../core/util';
import { backoffJobPollDelay } from '../../../core/jobs';
import JobStatusTable from './JobStatusTable';
import {
  ANALYSES_META_INFO,
  DEEPSEA_ANALYSES,
  DEEPSEA_MODELS,
} from '../../../settings';

function hasVizResult(job) {
  const dsModel = DEEPSEA_MODELS.find(
    model => model.name.toLowerCase() === job.ds_model?.toLowerCase(),
  );
  return (
    (job.input_type === 'fasta' && job.insilico) ||
    (dsModel && dsModel.vizInputTypes.includes(job.input_type))
  );
}

const DeepSEAJobPage = ({ id }) => {
  const selectedJob = useSelector(state => state.deepsea.selectedJob);
  const jobs = useSelector(state => state.deepsea.jobs);
  const dispatch = useDispatch();
  const [analysisShortName, setAnalysisShortName] = useState(
    DEEPSEA_ANALYSES.deepsea,
  );
  const [delay, setDelay] = useState(1000);

  const status = selectedJob && <JobStatusTable job={selectedJob} statusOnly />;
  const progress = (
    <ProgressBar
      label={`${ANALYSES_META_INFO[analysisShortName].name} job is currently running...`}
      style={{
        marginLeft: 'auto',
        marginRight: 'auto',
      }}
    />
  );

  useEffect(() => {
    if (selectedJob && Object.keys(selectedJob).length)
      setAnalysisShortName(
        selectedJob.insilico
          ? DEEPSEA_ANALYSES.ism
          : selectedJob.expecto
          ? DEEPSEA_ANALYSES.expecto
          : DEEPSEA_ANALYSES.deepsea,
      );

    setDelay(
      selectedJob &&
        selectedJob.status &&
        (selectedJob.status === 'completed' ||
          selectedJob.status.startsWith('error'))
        ? null
        : 1000,
    );
  }, [selectedJob]);

  useEffect(() => {
    if (selectedJob && jobs && Object.keys(jobs).length) {
      if (!Object.keys(selectedJob).length && jobs[id]) {
        // in case of browser refresh
        dispatch(setSelectedDeepSEAJob(jobs[id]));
      } else if (
        jobs[selectedJob.job_id] &&
        selectedJob.status !== jobs[selectedJob.job_id].status
      ) {
        // in case status is not current
        dispatch(setSelectedDeepSEAJob(jobs[selectedJob.job_id]));
      }
    }
  }, [jobs]);

  useInterval(() => {
    dispatch(fetchDeepSEAJobStatus(id)).then(isSuccessful => {
      // A received job resets the delay above; failed checks retain its status.
      if (!isSuccessful) setDelay(backoffJobPollDelay);
    });
  }, delay);

  return !selectedJob || selectedJob.status !== 'completed' ? (
    <Grid fluid={false}>
      <Row>
        <Col md={12}>
          <div style={{ margin: 'auto', marginTop: 60, textAlign: 'center' }}>
            {selectedJob &&
              selectedJob.status &&
              !selectedJob.status.startsWith('error') &&
              progress}
          </div>
          {selectedJob && selectedJob.status && (
            <div style={{ width: '80%', margin: 'auto' }}>{status}</div>
          )}
        </Col>
      </Row>
    </Grid>
  ) : selectedJob && selectedJob.insilico ? (
    <DeepSEAInsilicoPage id={id} />
  ) : selectedJob && hasVizResult(selectedJob) ? (
    <DeepSEABrowserPage jobName={id} />
  ) : (
    // Handle completed jobs that don't support visualization
    <Grid fluid={false}>
      <Row>
        <Col md={12}>
          <div style={{ width: '80%', margin: 'auto', marginTop: 65 }}>
            <JobStatusTable job={selectedJob} />
          </div>
        </Col>
      </Row>
    </Grid>
  );
};

DeepSEAJobPage.propTypes = {
  id: PropTypes.string.isRequired,
};

export default DeepSEAJobPage;
